import React, { useMemo } from "react";
import { ATMGrid } from "components/atoms/ATMGrid/ATMGrid.component";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Chip } from "@mui/material";
import { ATMLoader } from "components/atoms/ATMLoader/ATMLoader.component";
import { FumigationCompanyDetail } from "services/fumigationCompany.types";
import moment from "moment";

interface DeletedFumigationCompaniesProps {
    /** Full record list passed down from AdminPortalPage (already cached). */
    allRecords: FumigationCompanyDetail[];
    isLoading: boolean;
}

const DeletedFumigationCompanies: React.FC<DeletedFumigationCompaniesProps> = ({ allRecords, isLoading }) => {
    // Derive deleted rows from the shared cached list — no extra fetch needed.
    const rows = useMemo(
        () => allRecords.filter(item => (item.status ?? '').toUpperCase() === 'DELETED'),
        [allRecords]
    );

    const columns: GridColDef[] = [
        { field: 'fumCompanyNm', headerName: 'Company Name', flex: 1, minWidth: 200 },
        { field: 'fumPhoneNbr', headerName: 'Phone Number', width: 160 },
        { field: 'fumNotes', headerName: 'Notes', flex: 2, minWidth: 250 },
        {
            field: 'updateDttm',
            headerName: 'Last Updated',
            width: 180,
            valueFormatter: (value: any) => {
                if (!value) return '';
                const parsed = moment(value);
                return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A') : value;
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => <Chip label={params.value} color="error" size="small" />,
        },
    ];

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <ATMLoader active inline="centered">Loading Deleted Fumigation Companies...</ATMLoader>
            </Box>
        );
    }

    return (
        <ATMGrid style={{ margin: 4 }}>
            <ATMGrid.Row>
                <ATMGrid.Column width={16}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.fumCompanyNm}
                        initialState={{
                            pagination: { paginationModel: { page: 0, pageSize: 10 } },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        disableRowSelectionOnClick
                        disableColumnMenu={false}
                        sx={{
                            minHeight: 400,
                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#f5f5f5' },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#fafafa' },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#e3f2fd' },
                        }}
                    />
                </ATMGrid.Column>
            </ATMGrid.Row>
        </ATMGrid>
    );
};

export { DeletedFumigationCompanies };
